import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import { useDispatch } from 'react-redux';
import CardFace from '../Card/CardFace';
import { PlainButton } from '../Controls';
import useCardDrag from '../Drag/useCardDrag';
import { TRAINER } from './handCardAction';
import { AT_ONCE, ON_A_POKEMON } from '../Trainer/trainerPlay';
import { picksCancel, startPicks, submitPicks } from '../Picks/actions';
import { closeContextMenu } from '../ActiveGame/actions';
import { Shake } from './styled';

/**
 * A card in your hand you can tap (for its menu) or drag onto the board (useCardDrag). Dragging starts the card's
 * flow, which lights up where it can go, the same as tapping its menu button; letting go on a lit spot does what
 * tapping that spot would. A Trainer is dragged onto the Pokémon it goes on, or anywhere on the board when it goes
 * on none (trainerPlay). A card that can't be played now shakes and says why when you try to drag it.
 */
const DraggableHandCard = ({ card, place, action, onTap, onRefused }) => {
  const dispatch = useDispatch();
  const handPosition = card.get('order');
  const trainer = action.kind === TRAINER ? action.play : null;

  // A Trainer starts the flow its menu button would: picking a Pokémon on the board, or nothing yet and played (or
  // its menu opened) wherever it's dropped on the board (dropAnywhere).
  const liftTrainer = () => {
    const { how, source, steps } = trainer;
    if (how === ON_A_POKEMON) {
      onTap();
      dispatch(startPicks(source, steps));
      return { kind: TRAINER, handPosition };
    }
    const dropAnywhere = how === AT_ONCE
      ? () => { dispatch(submitPicks(source)); }
      : () => {
        onTap();
        dispatch(startPicks(source, steps));
        return false;
      };
    return { kind: TRAINER, handPosition, dropAnywhere };
  };

  const { bind, ghost, refused } = useCardDrag({
    enabled: !action.reason,
    refusal: action.reason || null,
    place,
    lift: () => {
      if (trainer) {
        return liftTrainer();
      }
      if (action.start) {
        dispatch(action.start());
      }
      return { kind: action.kind, handPosition };
    },
    onMiss: () => {
      if (trainer && trainer.how === ON_A_POKEMON) {
        dispatch(picksCancel());
        dispatch(closeContextMenu());
      } else if (action.cancel) {
        dispatch(action.cancel());
      }
    },
    // Quick, nearly flat swipes scroll the hand.
    touchAction: 'pan-x',
  });

  useEffect(() => {
    onRefused(refused);
  }, [refused, onRefused]);

  return (
    <>
      <PlainButton {...bind} onClick={onTap} aria-label={card.get('name')}>
        <Shake $on={!!refused}><CardFace card={card} /></Shake>
      </PlainButton>
      {ghost(<CardFace card={card} />)}
    </>
  );
};

DraggableHandCard.propTypes = {
  card: PropTypes.instanceOf(Map).isRequired,
  /** The card's place on screen (Motion/anchorNames), so a card you drop doesn't fly there again. */
  place: PropTypes.string.isRequired,
  action: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    reason: PropTypes.string,
    start: PropTypes.func,
    cancel: PropTypes.func,
    play: PropTypes.object,
  }).isRequired,
  onTap: PropTypes.func.isRequired,
  /** Told why the card can't be played when you try to drag it, and null once that's said. */
  onRefused: PropTypes.func.isRequired,
};

export default DraggableHandCard;
